import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { ToasterService } from '../../providers/toaster/toaster.service';
import { PrivacyPolicyService } from './privacy-policy.service';

@Component({
  selector: 'app-privacy-policy',
  standalone: true,
  imports: [CommonModule, FormsModule, CKEditorModule],
  templateUrl: './privacy-policy.component.html',
  styleUrls: ['./privacy-policy.component.css']
})
export class PrivacyPolicyComponent implements OnInit {

  // Section selection
  selectedOption: 'about-us' | 'privacy-policy' = 'privacy-policy';

  // Content storage
  aboutUsContent = '';
  privacyPolicyContent = '';

  // CKEditor
  public Editor:any = ClassicEditor;
  isLoading = false;
  isSaving = false;
  pages: any[] = [];

  constructor(
    private privacyPolicyService: PrivacyPolicyService,
    private toasterService: ToasterService
  ) {}

  public editorConfig: any = {
  toolbar: {
    items: [
      'heading',
      '|',
      'fontSize',
      'fontFamily',
      'fontColor',
      'fontBackgroundColor',
      '|',
      'bold',
      'italic',
      'underline',
      'strikethrough',
      'subscript',
      'superscript',
      '|',
      'alignment',
      '|',
      'bulletedList',
      'numberedList',
      'outdent',
      'indent',
      '|',
      'link',
      'blockQuote',
      'insertTable',
      'imageUpload',
      '|',
      'undo',
      'redo'
    ]
  },

  heading: {
    options: [
      { model: 'paragraph', title: 'Normal', class: 'ck-heading_paragraph' },
      { model: 'heading1', view: 'h1', title: 'Heading 1' },
      { model: 'heading2', view: 'h2', title: 'Heading 2' },
      { model: 'heading3', view: 'h3', title: 'Heading 3' },
      { model: 'heading4', view: 'h4', title: 'Heading 4' }
    ]
  },

  fontSize: {
    options: [10, 12, 14, 'default', 18, 20, 24, 28]
  },

  fontFamily: {
    options: [
      'default',
      'Arial, Helvetica, sans-serif',
      'Courier New, Courier, monospace',
      'Georgia, serif',
      'Tahoma, Geneva, sans-serif',
      'Times New Roman, Times, serif',
      'Verdana, Geneva, sans-serif'
    ]
  },

  alignment: {
    options: ['left', 'center', 'right', 'justify']
  },

  link: {
    decorators: {
      openInNewTab: {
        mode: 'manual',
        label: 'Open in new tab',
        attributes: {
          target: '_blank',
          rel: 'noopener noreferrer'
        }
      }
    }
  },

  table: {
    contentToolbar: [
      'tableColumn',
      'tableRow',
      'mergeTableCells',
      'tableProperties',
      'tableCellProperties'
    ]
  },

  image: {
    toolbar: [
      'imageTextAlternative',
      'imageStyle:inline',
      'imageStyle:block',
      'imageStyle:side'
    ]
  },

  placeholder: 'Start typing document content here...',
  language: 'en'
};

  ngOnInit(): void {
    this.getPages();
  }


  // Dynamic getter/setter for editor binding
  get currentContent(): string {
    return this.selectedOption === 'about-us'
      ? this.aboutUsContent
      : this.privacyPolicyContent;
  }

  set currentContent(value: string) {
    if (this.selectedOption === 'about-us') {
      this.aboutUsContent = value;
    } else {
      this.privacyPolicyContent = value;
    }
  }

  onSectionChange(): void {
    this.mapPageContentToEditor();
  }

  saveContent(): void {
    const selectedPage = this.getSelectedPage();

    const payload = {
      section: this.selectedOption,
      slug: this.selectedOption,
      page_name: this.selectedOption === 'about-us' ? 'About Us' : 'Privacy Policy',
      title: this.selectedOption === 'about-us' ? 'About Us' : 'Privacy Policy',
      type: this.selectedOption,
      content: this.currentContent
    };

    this.isSaving = true;
    this.privacyPolicyService.savePageContent(payload, selectedPage?.id || selectedPage?.page_id).subscribe({
      next: () => {
        this.toasterService.setToaster('success', 'Success', 'Content saved successfully');
        this.isSaving = false;
        this.getPages();
      },
      error: () => {
        this.toasterService.setToaster('error', 'Error', 'Failed to save content');
      },
      complete: () => {
        this.isSaving = false;
      }
    });
  }

  resetContent(): void {
    if (this.selectedOption === 'about-us') {
      this.aboutUsContent = '';
    } else {
      this.privacyPolicyContent = '';
    }
  }

  private getPages(): void {
    this.isLoading = true;
    this.privacyPolicyService.getAllPages().subscribe({
      next: (res: any) => {
        this.pages = res?.body?.data || res?.data || [];
        this.mapPageContentToEditor();
      },
      error: () => {
        this.toasterService.setToaster('error', 'Error', 'Failed to load pages');
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  private mapPageContentToEditor(): void {
    if (!this.pages?.length) {
      return;
    }

    const aboutUsPage = this.findPageByKeys(this.getKeysForSection('about-us'));
    const privacyPolicyPage = this.findPageByKeys(this.getKeysForSection('privacy-policy'));

    this.aboutUsContent = aboutUsPage?.content || this.aboutUsContent || '';
    this.privacyPolicyContent = privacyPolicyPage?.content || this.privacyPolicyContent || '';
  }

  private findPageByKeys(keys: string[]): any {
    const normalizedKeys = keys.map((key) => key.toLowerCase());
    return this.pages.find((page: any) => {
      const candidates = [
        page?.slug,
        page?.page_name,
        page?.name,
        page?.title,
        page?.type,
        page?.Type
      ]
        .filter(Boolean)
        .map((value: string) => value.toLowerCase());

      return candidates.some((value: string) =>
        normalizedKeys.some((key) => value.includes(key))
      );
    });
  }

  private getSelectedPage(): any {
    return this.findPageByKeys(this.getKeysForSection(this.selectedOption));
  }

  private getKeysForSection(section: 'about-us' | 'privacy-policy'): string[] {
    return section === 'about-us'
      ? ['about-us', 'about us']
      : ['privacy-policy', 'privacy policy'];
  }
}
